export type PreviewCardRenderLifecycleStages = {
	readonly STARTED: 'started';
	readonly ACTION_PREPARE: 'actionPrepare';
	readonly ACTION_PART: 'actionPart';
	readonly ACTION_COMPLETE: 'actionComplete';
};

const lifecycleStage: PreviewCardRenderLifecycleStages = {
	STARTED: 'started',
	ACTION_PREPARE: 'actionPrepare',
	ACTION_PART: 'actionPart',
	ACTION_COMPLETE: 'actionComplete',
};

export type PreviewCardRenderLifecycleStage =
	PreviewCardRenderLifecycleStages[keyof PreviewCardRenderLifecycleStages];

export type PreviewCardRenderLifecycleSnapshot = {
	startedAtMs: number;
	latencyBudgetMs?: number;
	latestLifecycleStage: PreviewCardRenderLifecycleStage;
};

type PreviewCardRenderRequest = {
	conversationId: string;
	messageId: string;
};

type StartPreviewCardRenderLifecycle = PreviewCardRenderRequest & {
	startedAtMs: number;
	latencyBudgetMs?: number;
};

type UpdatePreviewCardRenderLifecycle = PreviewCardRenderRequest & {
	latestLifecycleStage: PreviewCardRenderLifecycleStage;
};

const MAX_TRACKED_REQUESTS = 100;
const lifecycleByRequest = new Map<string, PreviewCardRenderLifecycleSnapshot>();

const getRequestKey = ({ conversationId, messageId }: PreviewCardRenderRequest): string =>
	JSON.stringify([conversationId, messageId]);

const setLifecycleSnapshot = (
	requestKey: string,
	snapshot: PreviewCardRenderLifecycleSnapshot,
): void => {
	// Refresh insertion order so eviction keeps the requests that changed most recently.
	lifecycleByRequest.delete(requestKey);
	lifecycleByRequest.set(requestKey, snapshot);

	if (lifecycleByRequest.size > MAX_TRACKED_REQUESTS) {
		const oldestRequestKey = lifecycleByRequest.keys().next().value;
		if (oldestRequestKey !== undefined) {
			lifecycleByRequest.delete(oldestRequestKey);
		}
	}
};

const start = ({
	conversationId,
	messageId,
	startedAtMs,
	latencyBudgetMs,
}: StartPreviewCardRenderLifecycle): void => {
	const requestKey = getRequestKey({ conversationId, messageId });
	if (lifecycleByRequest.has(requestKey)) {
		return;
	}

	setLifecycleSnapshot(requestKey, {
		startedAtMs,
		latencyBudgetMs,
		latestLifecycleStage: lifecycleStage.STARTED,
	});
};

const update = ({
	conversationId,
	messageId,
	latestLifecycleStage,
}: UpdatePreviewCardRenderLifecycle): void => {
	const requestKey = getRequestKey({ conversationId, messageId });
	const lifecycle = lifecycleByRequest.get(requestKey);
	if (!lifecycle) {
		return;
	}

	setLifecycleSnapshot(requestKey, {
		...lifecycle,
		latestLifecycleStage,
	});
};

const get = ({
	conversationId,
	messageId,
}: PreviewCardRenderRequest): PreviewCardRenderLifecycleSnapshot | undefined => {
	const lifecycle = lifecycleByRequest.get(getRequestKey({ conversationId, messageId }));
	return lifecycle ? { ...lifecycle } : undefined;
};

export type PreviewCardRenderLifecycleApi = {
	featureGate: 'aifc_preview_card_mount_event';
	stage: PreviewCardRenderLifecycleStages;
	start: (input: StartPreviewCardRenderLifecycle) => void;
	update: (input: UpdatePreviewCardRenderLifecycle) => void;
	get: (input: PreviewCardRenderRequest) => PreviewCardRenderLifecycleSnapshot | undefined;
};

export const previewCardRenderLifecycle: PreviewCardRenderLifecycleApi = {
	featureGate: 'aifc_preview_card_mount_event',
	stage: lifecycleStage,
	start: start,
	update: update,
	get: get,
};
